import i18n from '@/core/i18n';
import { minutesToHours } from '@/helpers/date-format';

import {
  CORPORATE_FUNCTION_CAPACITY_FIELD,
  TeamMemberFieldNameDTO,
} from '../../types/dto/team-member-changed-field.dto';

/**
 * Gets the localized value for a value key
 */
export const getLocalizedValueKey = (key: string, t: (key: string) => string): string => {
  const translationKey = `details.tabs.changes.valueKeys.${key}`;
  const translated = t(translationKey);
  // If translation returns the key itself, it means no translation found, return original
  return translated === translationKey ? key : translated;
};

/**
 * Formats a field value for display
 * Handles booleans, dates, and primitives
 */
export const formatFieldValue = (
  value: string | null,
  t: (key: string) => string,
  fieldName?: string
): string => {
  if (!value) return '';

  // Handle boolean fields (IS_MANAGER, ON_DEMAND, IS_HOURLY_RATED)
  if (
    fieldName === TeamMemberFieldNameDTO.IS_MANAGER ||
    fieldName === TeamMemberFieldNameDTO.ON_DEMAND ||
    fieldName === TeamMemberFieldNameDTO.IS_HOURLY_RATED
  ) {
    if (value === 'true' || value === 'True') {
      return t('common:yes');
    }
    if (value === 'false' || value === 'False') {
      return t('common:no');
    }
  }

  // Handle CAPACITY and the derived CORPORATE_FUNCTION_CAPACITY — append % sign and round to max 2 decimal places
  if (
    fieldName === TeamMemberFieldNameDTO.CAPACITY ||
    fieldName === CORPORATE_FUNCTION_CAPACITY_FIELD
  ) {
    const num = Number(value);
    if (!isNaN(num)) {
      return `${parseFloat(num.toFixed(2))}%`;
    }
  }

  // Handle TOTAL_WORKING_MINUTES — convert minutes to hours and append "h" suffix
  if (fieldName === TeamMemberFieldNameDTO.TOTAL_WORKING_MINUTES) {
    const minutes = Number(value);
    if (!isNaN(minutes)) {
      return `${minutesToHours(minutes)}h`;
    }
  }

  // Check if it's a date (ISO format)
  if (value.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(value)) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
      }
    } catch {
      // Fall through to try other formats
    }
  }

  // Try to get localized value if it looks like a key
  const localizedValue = getLocalizedValueKey(value, t);
  if (localizedValue !== value) {
    return localizedValue;
  }

  return value;
};

/**
 * Formats a value with currency
 */
export const formatValueWithCurrency = (value: string, currency: string | null): string => {
  if (!currency) return value;
  return i18n.t('authenticated.team:details.tabs.changes.valueDisplay.withCurrency', {
    value,
    currency,
  });
};
